import { type POSSIBLE_METRICS_ENUM } from "constants/metrics"
import { Box, type BoxProps } from "@tokenterminal/ui/Box"
import { theme } from "@tokenterminal/ui/theme"
import { getTooltipProps } from "modules/tooltip/getTooltipProps"
import { useTooltipContent } from "modules/tooltip/useTooltipContent"
import { type SVGProps, forwardRef } from "react"
import { type ProjectType } from "types/ApiTypes"
import { notAvailableIconStyle } from "./MetricNotAvailable.css"
import InfoIcon from "@/icons/info.svg"

const resolveDataNotAvailableTooltipId = (
  project?: ProjectType | MinimumProjectInfo,
  id?: string
) => {
  /*
   * Determine correct tooltip id. This depends on the metric and the state of the token.
   * Eg. If token is not launched, it's natural for us to have no data about its certain metrics.
   * Some metrics are independent from other metrics, hence project info is optional.
   */

  switch (id) {
    case "market_cap_fully_diluted": {
      if (!project) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            `Cannot definitely determine correct Not Available -tooltip due to missing project for metric "${id}". Defaulting to best scenario.`
          )
        }
        return "not-diluted"
      }
      const hasPrice = !!(
        project.metric_aggregations?.price &&
        Object.values(project.metric_aggregations?.price).length
      )
      if (!hasPrice) {
        return "not-launched"
      }

      const hasReachedMarketCap = !!(
        project.metric_aggregations?.market_cap_circulating &&
        Object.values(project.metric_aggregations?.market_cap_circulating)
          .length
      )

      return hasReachedMarketCap ? "not-diluted" : "default"
    }
    case "market_cap_circulating":
    case "price":
    case "price_24h_change":
      return "not-launched"
    case "ps":
    case "pf": {
      return "not-available"
    }
    case "custom-metric-not-found": {
      return id
    }
    default: {
      if (id) {
        /* In case dealing with change/trend or any ratio metrics, use different message.
         * The default gives assumption that something is to be done by us or someone. In case
         * of change data, it can just be that meaningful data is not available or cant ever be available for whatever reason
         * Eg. Token is a week old and hence can't have change data from 180d or whatever
         */
        if (id.includes("_change") || id.includes("_trend")) {
          return "not-available"
        }
        // Same with ratio metrics
        if (id.includes("ps") || id.includes("pf") || id.includes("pe")) {
          return "not-available"
        }
      }
      return "default"
    }
  }
}

type MinimumProjectInfo = {
  metric_aggregations: Pick<
    NonNullable<ProjectType["metric_aggregations"]>,
    POSSIBLE_METRICS_ENUM.PRICE | POSSIBLE_METRICS_ENUM.MARKET_CAP_CIRCULATING
  >
}

interface MetricNotAvailableProps extends Pick<BoxProps, "color"> {
  metricName?: string | POSSIBLE_METRICS_ENUM
  project?: ProjectType | MinimumProjectInfo
  iconColor?: string
  fontSize?: "s" | "m" | "xs" | undefined
}

const NotAvailable = forwardRef<
  HTMLElement,
  {
    iconProps?: SVGProps<SVGElement>
    iconColor?: SVGProps<SVGElement>["color"]
  } & BoxProps
>(({ iconProps, iconColor, children, ...boxProps }, ref) => (
  <Box
    display="inlineFlex"
    alignItems="center"
    gap="xxs"
    as="span" // Can be rendered inside <p>
    ref={ref}
    {...boxProps}
  >
    {children || "N/A"}
    <InfoIcon
      color={iconColor ?? theme.primary.color}
      width=".75rem"
      className={notAvailableIconStyle}
      role="presentation"
      {...iconProps}
    />
  </Box>
))

NotAvailable.displayName = "NotAvailable"

export const MetricNotAvailable = ({
  metricName,
  project,
  children,
  color,
  iconColor,
  fontSize,
  className,
  ...iconProps
}: MetricNotAvailableProps & SVGProps<SVGElement>) => {
  const tooltip = useTooltipContent({
    id: resolveDataNotAvailableTooltipId(project, metricName),
    projectId: "data-not-available",
  })
  return (
    <NotAvailable
      className={className}
      fontSize={fontSize}
      color={color}
      iconProps={iconProps}
      iconColor={iconColor}
      {...getTooltipProps(tooltip, true)}
    >
      {children}
    </NotAvailable>
  )
}
